import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Plus, ChevronDown, ChevronUp, Search,
  MessageSquare, Clock, AlertTriangle, CheckCircle, XCircle,
  User, Store, FileText, Tag, Filter, Send, Flag
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { API_URL } from '../../utils/apiConfig';

const PriorityBadge = ({ priority }) => {
  const colors = {
    low: 'bg-slate-600 text-slate-200',
    medium: 'bg-blue-600 text-blue-100',
    high: 'bg-orange-600 text-orange-100',
    urgent: 'bg-red-600 text-red-100 animate-pulse',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${colors[priority] || 'bg-slate-600'}`}>
      {priority}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const colors = {
    new: 'bg-blue-600 text-blue-100',
    assigned: 'bg-purple-600 text-purple-100',
    in_progress: 'bg-cyan-600 text-cyan-100',
    waiting_customer: 'bg-amber-600 text-amber-100',
    waiting_internal: 'bg-amber-600 text-amber-100',
    escalated: 'bg-red-600 text-red-100',
    resolved: 'bg-emerald-600 text-emerald-100',
    closed: 'bg-slate-500 text-slate-200',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || 'bg-slate-600'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const SLAIndicator = ({ slaStatus }) => {
  if (!slaStatus) return null;
  
  if (slaStatus.status === 'breached') {
    return (
      <span className="text-red-400 text-xs flex items-center gap-1">
        <Clock className="w-3 h-3" />
        {slaStatus.overdue_hours}h overdue
      </span>
    );
  }
  
  if (slaStatus.status === 'on_track') {
    const hours = slaStatus.hours_remaining;
    const color = hours < 2 ? 'text-orange-400' : hours < 8 ? 'text-amber-400' : 'text-emerald-400';
    return (
      <span className={`${color} text-xs flex items-center gap-1`}>
        <Clock className="w-3 h-3" />
        {hours}h remaining
      </span>
    );
  }
  
  return null;
};

export default function AdminSupportView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedId, setExpandedId] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Create case form
  const [createForm, setCreateForm] = useState({
    category: 'general_inquiry',
    priority: 'medium',
    subject: '',
    description: '',
    reporter_email: '',
    reporter_name: '',
    source: 'internal'
  });
  
  // Note form
  const [noteForm, setNoteForm] = useState({
    note: '',
    is_internal: true,
    notify_customer: false
  });
  
  // Resolve form
  const [resolveForm, setResolveForm] = useState({
    resolution_summary: '',
    customer_satisfied: null,
    follow_up_required: false
  });

  const getToken = () => localStorage.getItem('pecash_token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/support/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  }, []);

  const fetchCases = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/support/cases?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (priorityFilter) url += `&priority=${priorityFilter}`;
      if (categoryFilter) url += `&category=${categoryFilter}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCases(data.cases || []);
      }
    } catch (err) {
      console.error('Cases fetch error:', err);
    }
  }, [statusFilter, priorityFilter, categoryFilter, searchTerm]);

  const fetchCaseDetail = async (caseId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/support/cases/${caseId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedCase(data);
      }
    } catch (err) {
      console.error('Case detail fetch error:', err);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchCases()]);
      setLoading(false);
    };
    load();
  }, [fetchDashboard, fetchCases]);

  useEffect(() => {
    fetchCases();
  }, [statusFilter, priorityFilter, categoryFilter, searchTerm, fetchCases]);

  const handleCreateCase = async () => {
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/support/cases`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setShowCreateModal(false);
        setCreateForm({
          category: 'general_inquiry',
          priority: 'medium',
          subject: '',
          description: '',
          reporter_email: '',
          reporter_name: '',
          source: 'internal'
        });
        setSuccessMsg(`Case ${data.case_number} created successfully`);
        await Promise.all([fetchDashboard(), fetchCases()]);
        setActiveTab('cases');
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to create case');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleUpdateCase = async (caseId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/support/cases/${caseId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        await Promise.all([fetchDashboard(), fetchCases()]);
        if (selectedCase) {
          await fetchCaseDetail(caseId);
        }
      } else {
        const err = await res.json();
        setError(err.detail || 'Update failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleAddNote = async () => {
    if (!selectedCase) return;
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/support/cases/${selectedCase.case.id}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(noteForm)
      });
      
      if (res.ok) {
        setShowNoteModal(false);
        setNoteForm({ note: '', is_internal: true, notify_customer: false });
        await fetchCaseDetail(selectedCase.case.id);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to add note');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleResolve = async () => {
    if (!selectedCase) return;
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/support/cases/${selectedCase.case.id}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(resolveForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setShowResolveModal(false);
        setResolveForm({ resolution_summary: '', customer_satisfied: null, follow_up_required: false });
        setSuccessMsg(`Case resolved. SLA ${data.sla_met ? 'met' : 'breached'}. Resolution time: ${data.resolution_time_hours}h`);
        await Promise.all([fetchDashboard(), fetchCases()]);
        await fetchCaseDetail(selectedCase.case.id);
        setTimeout(() => setSuccessMsg(''), 5000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to resolve');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleEscalate = async (caseId) => {
    const reason = prompt('Enter escalation reason (min 10 characters):');
    if (!reason || reason.length < 10) {
      setError('Escalation reason must be at least 10 characters');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/support/cases/${caseId}/escalate?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (res.ok) {
        await Promise.all([fetchDashboard(), fetchCases()]);
        if (selectedCase) {
          await fetchCaseDetail(caseId);
        }
        setSuccessMsg('Case escalated to URGENT priority');
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Escalation failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-emerald-400" />
                Support Cases
              </h1>
              <p className="text-sm text-slate-400">Customer & merchant issue tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dashboard?.statistics?.sla_breached > 0 && (
              <span className="px-3 py-1 bg-red-900/50 border border-red-700 rounded-full text-red-300 text-xs font-medium animate-pulse">
                {dashboard.statistics.sla_breached} SLA BREACHED
              </span>
            )}
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="support-create-case"
            >
              <Plus className="w-4 h-4 mr-2" /> New Case
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Success/Error Messages */}
        {successMsg && (
          <div className="bg-emerald-900/30 border border-emerald-700 rounded-xl p-4 mb-6">
            <p className="text-emerald-400 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> {successMsg}
            </p>
          </div>
        )}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-6">
            <p className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
            <Button size="sm" variant="ghost" className="mt-2 text-red-400" onClick={() => setError('')}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-blue-700/50 rounded-xl p-4"
            >
              <p className="text-blue-400 text-xs mb-1">New Cases</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.by_status?.new || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800 border border-cyan-700/50 rounded-xl p-4"
            >
              <p className="text-cyan-400 text-xs mb-1">In Progress</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.by_status?.in_progress || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800 border border-red-700/50 rounded-xl p-4"
            >
              <p className="text-red-400 text-xs mb-1">SLA Breached</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.sla_breached || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800 border border-emerald-700/50 rounded-xl p-4"
            >
              <p className="text-emerald-400 text-xs mb-1">Resolved (Week)</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.resolved_this_week || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-800 border border-amber-700/50 rounded-xl p-4"
            >
              <p className="text-amber-400 text-xs mb-1">Avg Resolution</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.avg_resolution_hours || '-'}h</p>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          <Button
            data-testid="support-tab-dashboard"
            variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
            className={activeTab === 'dashboard' ? 'bg-emerald-600' : 'text-slate-400'}
            onClick={() => { setActiveTab('dashboard'); setSelectedCase(null); }}
          >
            <FileText className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <Button
            data-testid="support-tab-cases"
            variant={activeTab === 'cases' ? 'default' : 'ghost'}
            className={activeTab === 'cases' ? 'bg-emerald-600' : 'text-slate-400'}
            onClick={() => { setActiveTab('cases'); setSelectedCase(null); }}
          >
            <MessageSquare className="w-4 h-4 mr-1" /> All Cases ({cases.length})
          </Button>
          {selectedCase && (
            <Button
              data-testid="support-tab-detail"
              variant="default"
              className="bg-emerald-600"
            >
              <User className="w-4 h-4 mr-1" /> {selectedCase.case.case_number}
            </Button>
          )}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && !selectedCase && dashboard && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="support-dashboard-grid">
            {/* Urgent Cases */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6" data-testid="urgent-cases-card">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Urgent & High Priority ({dashboard.urgent_cases?.length || 0})
              </h3>
              {dashboard.urgent_cases?.length > 0 ? (
                <div className="space-y-2" data-testid="urgent-cases-list">
                  {dashboard.urgent_cases.map((c) => (
                    <div 
                      key={c.id} 
                      className="bg-slate-900 rounded-lg p-3 cursor-pointer hover:bg-slate-700/50"
                      onClick={() => { fetchCaseDetail(c.id); setActiveTab('detail'); }}
                      data-testid={`urgent-case-${c.case_number}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm font-medium">{c.case_number}</p>
                          <p className="text-slate-400 text-xs truncate max-w-[200px]">{c.subject}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <PriorityBadge priority={c.priority} />
                          <StatusBadge status={c.status} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4" data-testid="no-urgent-cases">No urgent cases - great!</p>
              )}
            </div>

            {/* SLA Breached Cases */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6" data-testid="sla-breached-card">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-red-400" />
                SLA Breached ({dashboard.sla_breached_cases?.length || 0})
              </h3>
              {dashboard.sla_breached_cases?.length > 0 ? (
                <div className="space-y-2" data-testid="sla-breached-list">
                  {dashboard.sla_breached_cases.map((c) => (
                    <div 
                      key={c.id} 
                      className="bg-slate-900 rounded-lg p-3 cursor-pointer hover:bg-slate-700/50 border border-red-900/50"
                      onClick={() => { fetchCaseDetail(c.id); setActiveTab('detail'); }}
                      data-testid={`sla-case-${c.case_number}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm font-medium">{c.case_number}</p>
                          <p className="text-slate-400 text-xs">{c.reporter_email}</p>
                        </div>
                        <div className="text-red-400 text-xs">
                          Overdue since {new Date(c.sla_due_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-emerald-400 text-center py-4" data-testid="sla-on-track">All SLAs on track!</p>
              )}
            </div>

            {/* Recent Cases */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 lg:col-span-2" data-testid="recent-cases-card">
              <h3 className="text-white font-semibold mb-4">Recent Cases</h3>
              {dashboard.recent_cases?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-slate-700">
                      <tr>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Case #</th>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Subject</th>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Category</th>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Priority</th>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Status</th>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">SLA</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {dashboard.recent_cases.map((c) => (
                        <tr 
                          key={c.id} 
                          className="hover:bg-slate-700/50 cursor-pointer"
                          onClick={() => { fetchCaseDetail(c.id); setActiveTab('detail'); }}
                        >
                          <td className="px-3 py-2 text-emerald-400 text-sm">{c.case_number}</td>
                          <td className="px-3 py-2 text-white text-sm truncate max-w-[200px]">{c.subject}</td>
                          <td className="px-3 py-2 text-slate-400 text-xs">{c.category?.replace(/_/g, ' ')}</td>
                          <td className="px-3 py-2"><PriorityBadge priority={c.priority} /></td>
                          <td className="px-3 py-2"><StatusBadge status={c.status} /></td>
                          <td className="px-3 py-2"><SLAIndicator slaStatus={c.sla_status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No cases yet</p>
              )}
            </div>
          </div>
        )}

        {/* Cases Tab */}
        {activeTab === 'cases' && !selectedCase && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search cases..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="waiting_customer">Waiting Customer</option>
                <option value="escalated">Escalated</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Categories</option>
                <option value="payment_issue">Payment Issue</option>
                <option value="redemption_issue">Redemption Issue</option>
                <option value="account_access">Account Access</option>
                <option value="merchant_onboarding">Merchant Onboarding</option>
                <option value="refund_request">Refund Request</option>
                <option value="dispute">Dispute</option>
                <option value="technical_issue">Technical Issue</option>
                <option value="general_inquiry">General Inquiry</option>
              </select>
            </div>

            {/* Cases Table */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Case</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Reporter</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Category</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Priority</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Status</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">SLA</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {cases.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-slate-500 py-8">
                        No support cases found
                      </td>
                    </tr>
                  ) : (
                    cases.map((c) => (
                      <tr 
                        key={c.id}
                        className="hover:bg-slate-700/50 cursor-pointer"
                        onClick={() => { fetchCaseDetail(c.id); setActiveTab('detail'); }}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-emerald-400 text-sm font-medium">{c.case_number}</p>
                            <p className="text-white text-xs truncate max-w-[200px]">{c.subject}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-sm">{c.reporter_email}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{c.category?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3"><PriorityBadge priority={c.priority} /></td>
                        <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                        <td className="px-4 py-3"><SLAIndicator slaStatus={c.sla_status} /></td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Case Detail View */}
        {selectedCase && (
          <div className="space-y-6">
            {/* Case Header */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-bold text-white">{selectedCase.case.case_number}</h2>
                    <PriorityBadge priority={selectedCase.case.priority} />
                    <StatusBadge status={selectedCase.case.status} />
                  </div>
                  <h3 className="text-lg text-white">{selectedCase.case.subject}</h3>
                  <p className="text-slate-400 text-sm mt-1">{selectedCase.case.category?.replace(/_/g, ' ')} • {selectedCase.case.source}</p>
                </div>
                <div className="flex gap-2">
                  {!['resolved', 'closed'].includes(selectedCase.case.status) && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-400"
                        onClick={() => setShowNoteModal(true)}
                      >
                        <MessageSquare className="w-4 h-4 mr-1" /> Add Note
                      </Button>
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => handleEscalate(selectedCase.case.id)}
                      >
                        <Flag className="w-4 h-4 mr-1" /> Escalate
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => setShowResolveModal(true)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Resolve
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-400"
                    onClick={() => { setSelectedCase(null); setActiveTab('cases'); }}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-slate-500 text-xs">Reporter</p>
                  <p className="text-white text-sm">{selectedCase.case.reporter_email}</p>
                  {selectedCase.case.reporter_name && (
                    <p className="text-slate-400 text-xs">{selectedCase.case.reporter_name}</p>
                  )}
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Created</p>
                  <p className="text-white text-sm">{new Date(selectedCase.case.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">SLA Due</p>
                  <p className="text-white text-sm">{new Date(selectedCase.case.sla_due_at).toLocaleString()}</p>
                  <SLAIndicator slaStatus={selectedCase.case.sla_status} />
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Assigned To</p>
                  <p className="text-white text-sm">{selectedCase.case.assigned_to || 'Unassigned'}</p>
                </div>
              </div>
              
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-xs mb-2">Description</p>
                <p className="text-white text-sm whitespace-pre-wrap">{selectedCase.case.description}</p>
              </div>

              {/* Quick Actions */}
              {!['resolved', 'closed'].includes(selectedCase.case.status) && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700">
                  <span className="text-slate-500 text-sm mr-2">Status:</span>
                  {['new', 'assigned'].includes(selectedCase.case.status) && (
                    <Button 
                      size="sm" 
                      className="bg-cyan-600 hover:bg-cyan-700"
                      onClick={() => handleUpdateCase(selectedCase.case.id, { status: 'in_progress' })}
                    >
                      Start Working
                    </Button>
                  )}
                  {selectedCase.case.status === 'in_progress' && (
                    <>
                      <Button 
                        size="sm" 
                        className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => handleUpdateCase(selectedCase.case.id, { status: 'waiting_customer' })}
                      >
                        Waiting Customer
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => handleUpdateCase(selectedCase.case.id, { status: 'waiting_internal' })}
                      >
                        Waiting Internal
                      </Button>
                    </>
                  )}
                  {['waiting_customer', 'waiting_internal'].includes(selectedCase.case.status) && (
                    <Button 
                      size="sm" 
                      className="bg-cyan-600 hover:bg-cyan-700"
                      onClick={() => handleUpdateCase(selectedCase.case.id, { status: 'in_progress' })}
                    >
                      Resume
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Related Entities */}
            {Object.keys(selectedCase.related_entities || {}).length > 0 && (
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">Related Entities</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {selectedCase.related_entities.sender && (
                    <div className="bg-slate-900 rounded-lg p-3">
                      <p className="text-slate-500 text-xs flex items-center gap-1"><User className="w-3 h-3" /> Sender</p>
                      <p className="text-white text-sm">{selectedCase.related_entities.sender.email}</p>
                    </div>
                  )}
                  {selectedCase.related_entities.merchant && (
                    <div className="bg-slate-900 rounded-lg p-3">
                      <p className="text-slate-500 text-xs flex items-center gap-1"><Store className="w-3 h-3" /> Merchant</p>
                      <p className="text-white text-sm">{selectedCase.related_entities.merchant.business_name || selectedCase.related_entities.merchant.email}</p>
                    </div>
                  )}
                  {selectedCase.related_entities.authorization && (
                    <div className="bg-slate-900 rounded-lg p-3">
                      <p className="text-slate-500 text-xs">Authorization</p>
                      <p className="text-white text-sm">{selectedCase.related_entities.authorization.reference_id}</p>
                      <p className="text-emerald-400 text-xs">${selectedCase.related_entities.authorization.usd_amount}</p>
                    </div>
                  )}
                  {selectedCase.related_entities.transaction && (
                    <div className="bg-slate-900 rounded-lg p-3">
                      <p className="text-slate-500 text-xs">Transaction</p>
                      <p className="text-white text-sm">{selectedCase.related_entities.transaction.id?.slice(0, 12)}...</p>
                      <p className="text-emerald-400 text-xs">{selectedCase.related_entities.transaction.status}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Notes ({selectedCase.notes?.length || 0})</h3>
                <Button size="sm" variant="ghost" className="text-emerald-400" onClick={() => setShowNoteModal(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Add Note
                </Button>
              </div>
              {selectedCase.notes?.length > 0 ? (
                <div className="space-y-3">
                  {selectedCase.notes.map((note) => (
                    <div key={note.id} className={`bg-slate-900 rounded-lg p-3 ${note.is_internal ? 'border-l-4 border-amber-500' : ''}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-xs">{note.added_by}</span>
                        <div className="flex items-center gap-2">
                          {note.is_internal && <span className="text-amber-400 text-xs">INTERNAL</span>}
                          <span className="text-slate-500 text-xs">{new Date(note.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                      <p className="text-white text-sm whitespace-pre-wrap">{note.note}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No notes yet</p>
              )}
            </div>

            {/* History */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Activity History</h3>
              {selectedCase.history?.length > 0 ? (
                <div className="space-y-2">
                  {selectedCase.history.map((h) => (
                    <div key={h.id} className="flex items-center gap-3 py-2 border-b border-slate-700 last:border-0">
                      <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                      <div className="flex-1">
                        <p className="text-white text-sm capitalize">{h.action.replace(/_/g, ' ')}</p>
                        {h.changes && Object.keys(h.changes).length > 0 && (
                          <p className="text-slate-500 text-xs">
                            Changed: {Object.keys(h.changes).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-xs">{h.performed_by}</p>
                        <p className="text-slate-500 text-xs">{new Date(h.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No history</p>
              )}
            </div>
          </div>
        )}

        {/* Create Case Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-emerald-400" />
                  Create Support Case
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Category *</label>
                      <select
                        value={createForm.category}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="general_inquiry">General Inquiry</option>
                        <option value="payment_issue">Payment Issue</option>
                        <option value="redemption_issue">Redemption Issue</option>
                        <option value="account_access">Account Access</option>
                        <option value="merchant_onboarding">Merchant Onboarding</option>
                        <option value="refund_request">Refund Request</option>
                        <option value="dispute">Dispute</option>
                        <option value="technical_issue">Technical Issue</option>
                        <option value="compliance">Compliance</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Priority</label>
                      <select
                        value={createForm.priority}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, priority: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="low">Low (72h SLA)</option>
                        <option value="medium">Medium (24h SLA)</option>
                        <option value="high">High (8h SLA)</option>
                        <option value="urgent">Urgent (2h SLA)</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Subject *</label>
                    <input
                      type="text"
                      value={createForm.subject}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      placeholder="Brief description of the issue"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Description *</label>
                    <textarea
                      value={createForm.description}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-24"
                      placeholder="Detailed description of the issue"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Reporter Email *</label>
                      <input
                        type="email"
                        value={createForm.reporter_email}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, reporter_email: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                        placeholder="customer@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Reporter Name</label>
                      <input
                        type="text"
                        value={createForm.reporter_name}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, reporter_name: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                        placeholder="John Doe"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Source</label>
                    <select
                      value={createForm.source}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, source: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="internal">Internal</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="chat">Chat</option>
                      <option value="self_service">Self-Service</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateCase}
                    disabled={!createForm.subject || !createForm.description || createForm.description.length < 10 || !createForm.reporter_email}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Case
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Note Modal */}
        {showNoteModal && selectedCase && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-400" />
                  Add Note to {selectedCase.case.case_number}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Note</label>
                    <textarea
                      value={noteForm.note}
                      onChange={(e) => setNoteForm(prev => ({ ...prev, note: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-32"
                      placeholder="Enter your note..."
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-slate-400 text-sm">
                      <input
                        type="checkbox"
                        checked={noteForm.is_internal}
                        onChange={(e) => setNoteForm(prev => ({ ...prev, is_internal: e.target.checked }))}
                        className="rounded"
                      />
                      Internal note (not visible to customer)
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowNoteModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddNote}
                    disabled={!noteForm.note}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Add Note
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resolve Modal */}
        {showResolveModal && selectedCase && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Resolve {selectedCase.case.case_number}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Resolution Summary *</label>
                    <textarea
                      value={resolveForm.resolution_summary}
                      onChange={(e) => setResolveForm(prev => ({ ...prev, resolution_summary: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-32"
                      placeholder="Describe how the issue was resolved..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Customer Satisfied?</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-slate-400 text-sm">
                        <input
                          type="radio"
                          name="satisfied"
                          checked={resolveForm.customer_satisfied === true}
                          onChange={() => setResolveForm(prev => ({ ...prev, customer_satisfied: true }))}
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-2 text-slate-400 text-sm">
                        <input
                          type="radio"
                          name="satisfied"
                          checked={resolveForm.customer_satisfied === false}
                          onChange={() => setResolveForm(prev => ({ ...prev, customer_satisfied: false }))}
                        />
                        No
                      </label>
                      <label className="flex items-center gap-2 text-slate-400 text-sm">
                        <input
                          type="radio"
                          name="satisfied"
                          checked={resolveForm.customer_satisfied === null}
                          onChange={() => setResolveForm(prev => ({ ...prev, customer_satisfied: null }))}
                        />
                        Unknown
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={resolveForm.follow_up_required}
                      onChange={(e) => setResolveForm(prev => ({ ...prev, follow_up_required: e.target.checked }))}
                      className="rounded"
                    />
                    <label className="text-slate-400 text-sm">Follow-up required</label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowResolveModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleResolve}
                    disabled={!resolveForm.resolution_summary || resolveForm.resolution_summary.length < 10}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Resolve Case
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
